import * as THREE from 'three';
import type { SoundStats } from './audio/engine.js';

/** The HUD's room for a 360° photo lesson's lines. */
const PANO_LINES = 2;

/**
 * `?perf=1` (ADR-032): frame time, draw calls and triangles, on a small
 * panel fixed in the view -- in the camera, like the fade -- so it shows in a
 * headset as well as on a laptop, where the budgets actually bite.
 */
export class PerfMeter {
  readonly #mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly #canvas: HTMLCanvasElement;
  readonly #texture: THREE.CanvasTexture;
  #frames = 0;
  #time = 0;
  #worst = 0;

  constructor(
    readonly camera: THREE.Object3D,
    readonly renderer: THREE.WebGLRenderer,
    /** A world's sound (ADR-034): its voices, and the master's peak and RMS, twice a second. */
    readonly sound?: () => SoundStats,
    /**
     * A 360° photo lesson's lines (ADR-041, pano-hud.ts): the GPU's caps, then what the photo
     * shown went up as. Room for two.
     */
    readonly pano?: () => readonly string[],
  ) {
    this.#canvas = document.createElement('canvas');
    this.#canvas.width = 512;
    this.#canvas.height = (sound ? 176 : 96) + (pano ? PANO_LINES * 40 : 0);
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.#mesh = new THREE.Mesh(
      // 0.32 m across, as tall as the canvas is for its width.
      new THREE.PlaneGeometry(0.32, (0.32 * this.#canvas.height) / 512),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        depthTest: false,
        toneMapped: false,
        fog: false,
      }),
    );
    // Up and to the left, out of the lesson's way; before the fade quad.
    this.#mesh.position.set(-0.24, 0.2, -0.62);
    this.#mesh.renderOrder = 998;
    camera.add(this.#mesh);
  }

  update(dt: number): void {
    this.#frames++;
    this.#time += dt;
    this.#worst = Math.max(this.#worst, dt);
    if (this.#time < 0.5) return;
    const ms = (this.#time / this.#frames) * 1000;
    const { calls, triangles } = this.renderer.info.render;
    const context = this.#canvas.getContext('2d');
    const height = this.#canvas.height;
    if (context) {
      context.clearRect(0, 0, 512, height);
      context.fillStyle = 'rgba(12, 16, 24, 0.75)';
      context.fillRect(0, 0, 512, height);
      context.fillStyle = ms > 1000 / 72 + 0.5 ? '#ff9a7a' : '#b8f0c0';
      context.font = '600 30px system-ui, sans-serif';
      const dutch = (n: number, places = 1): string => n.toFixed(places).replace('.', ',');
      context.fillText(
        `${dutch(ms)} ms (${dutch(1000 / ms, 0)} fps) · piek ${dutch(this.#worst * 1000)} ms`,
        16,
        40,
      );
      context.fillStyle = '#e8ecf2';
      context.fillText(
        `${calls} draw calls · ${Math.round(triangles).toLocaleString('nl-NL')} driehoeken`,
        16,
        80,
      );
      if (this.sound) {
        const { voices, hrtf, peakDb, rmsDb } = this.sound();
        const level = (db: number): string => (db < -99 ? '–' : `${dutch(db, 0)} dB`);
        context.fillText(`geluid: ${voices} stemmen, ${hrtf} HRTF`, 16, 120);
        context.fillText(`piek ${level(peakDb)} · rms ${level(rmsDb)}`, 16, 160);
      }
      if (this.pano) {
        const top = this.sound ? 176 : 96;
        context.font = '600 24px system-ui, sans-serif';
        context.fillStyle = '#ffe28a';
        this.pano()
          .slice(0, PANO_LINES)
          // Squeezed rather than cut off should a line run long (a slow upload's ms).
          .forEach((line, i) => context.fillText(line, 16, top + 28 + i * 40, 480));
      }
      this.#texture.needsUpdate = true;
    }
    this.#frames = 0;
    this.#time = 0;
    this.#worst = 0;
  }

  dispose(): void {
    this.#mesh.removeFromParent();
    this.#mesh.geometry.dispose();
    this.#mesh.material.dispose();
    this.#texture.dispose();
  }
}
